import { useTranslation } from '../../i18n/useTranslation';
import { Badge } from '../atoms/Badge/Badge';
import type { BadgeTone } from '../atoms/Badge/Badge.types';
import { Button } from '../atoms/Button/Button';
import { Inline } from '../atoms/Inline/Inline';
import { Stack } from '../atoms/Stack/Stack';
import { Text } from '../atoms/Typography/Text';
import { pointLabel } from '../../services/lightSurveyService';
import { formatRange, type FlowCoral, type FlowCoralAssessment, type FlowPoint, type FlowStatus } from '../../services/flowSurveyService';
import '../lighting/Lighting.css';

export const FLOW_STATUS_TONE: Readonly<Record<FlowStatus, BadgeTone>> = {
  ok: 'success',
  too_weak: 'warning',
  too_strong: 'warning',
  uncertain: 'info',
  unknown_group: 'neutral',
  no_data: 'neutral',
};

export function flowCoralName(coral: FlowCoral): string {
  return coral.livestockName ?? coral.label ?? '';
}

export interface FlowCoralListProps {
  corals: readonly FlowCoral[];
  assessments: readonly FlowCoralAssessment[];
  points: readonly FlowPoint[];
  busy: boolean;
  onEdit: (coral: FlowCoral) => void;
  onRemove: (coral: FlowCoral) => void;
}

/**
 * The accessible result of the flow placement check: per coral its number on
 * the photo, care group, the estimated range against what the group needs, the
 * verdict as text (never colour only), how sure it is, whether the direction
 * changes there and — when it is off — the nearest measured spot that fits.
 */
export function FlowCoralList({ corals, assessments, points, busy, onEdit, onRemove }: FlowCoralListProps) {
  const { t } = useTranslation();
  const byId = new Map(assessments.map((a) => [a.coralId, a]));
  const sequenceOf = new Map(points.map((p) => [p.id, p.sequence]));
  return (
    <ol className="lighting__list" data-testid="flow-coral-placements">
      {corals.map((coral, index) => {
        const a = byId.get(coral.id);
        const name = flowCoralName(coral);
        const status = a?.status ?? 'no_data';
        const suggestion = a?.suggestionPointId ? sequenceOf.get(a.suggestionPointId) : undefined;
        return (
          <li key={coral.id} className="lighting__row" data-testid={`flow-coral-${coral.id}`}>
            <Stack gap="1" className="lighting__row-text">
              <Inline gap="2" align="center">
                <Text weight="semibold">{t('flow.corals.itemTitle', { number: index + 1, name })}</Text>
                <Badge tone={FLOW_STATUS_TONE[status]}>{t(`flow.status.${status}`)}</Badge>
              </Inline>
              <Text color="secondary" size="sm">
                {coral.group ? t(`lighting.groups.${coral.group}`) : t('lighting.groups.none')}
                {coral.group && ` · ${String(t(`flow.needs.${coral.group}`))}`}
              </Text>
              {a && a.estimatedMin !== null && a.estimatedMax !== null && (
                <Text size="sm">
                  {t('flow.corals.estimate', { range: formatRange(a.estimatedMin, a.estimatedMax) })}
                  {a.confidence && ` · ${String(t(`lighting.confidence.${a.confidence}`))}`}
                  {a.alternating !== null && ` · ${String(t(a.alternating ? 'flow.corals.alternating' : 'flow.corals.oneDirection'))}`}
                </Text>
              )}
              {suggestion !== undefined && <Text size="sm">{t('flow.corals.suggestion', { point: pointLabel(suggestion) })}</Text>}
              {(status === 'too_weak' || status === 'too_strong') && suggestion === undefined && <Text size="sm">{t('flow.corals.noSpot')}</Text>}
              {status === 'uncertain' && <Text size="sm">{t('flow.corals.measureHere')}</Text>}
              {status === 'unknown_group' && <Text size="sm">{t('flow.corals.chooseGroup')}</Text>}
            </Stack>
            <Inline gap="2">
              <Button size="sm" variant="secondary" onClick={() => onEdit(coral)} disabled={busy} aria-label={t('flow.corals.editLabel', { name })}>
                {t('flow.edit')}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => onRemove(coral)} disabled={busy} aria-label={t('flow.corals.removeLabel', { name })}>
                {t('flow.remove')}
              </Button>
            </Inline>
          </li>
        );
      })}
    </ol>
  );
}

export default FlowCoralList;
